import {
  Newspaper,
  FileSearch,
  Globe,
  Video,
  ShieldAlert,
  type LucideIcon,
} from "lucide-react";

/**
 * Shared NovaSynth brand kit - one logo, one aurora backdrop, one set of
 * product-facing intel-agent definitions. Imported by the app shell, the
 * landing hero, and the auth/onboarding flows so the whole product feels
 * like a single, cohesive piece.
 *
 * NOTE: agents are branded by their JOB, never by any model / runtime.
 */

/** Animated NovaSynth mark - a synthesising "spark" inside a hex aperture. */
export function NovaMark({ className = "w-6 h-6", glow = true }: { className?: string; glow?: boolean }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      aria-hidden="true"
      style={glow ? { filter: "drop-shadow(0 0 6px rgba(34,211,238,0.45))" } : undefined}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 2.5l8.5 4.9v9.2L12 21.5 3.5 16.6V7.4L12 2.5z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 7.2l.9 3.1a2.4 2.4 0 0 0 1.8 1.7l3.1.8-3.1.8a2.4 2.4 0 0 0-1.8 1.7L12 19.2l-.9-3.2a2.4 2.4 0 0 0-1.8-1.7l-3.1-.8 3.1-.8a2.4 2.4 0 0 0 1.8-1.7L12 7.2z"
      />
    </svg>
  );
}

/** Brand wordmark - gradient lettering, optional mono tagline. */
export function Wordmark({
  size = "md",
  showTagline = false,
}: {
  size?: "sm" | "md" | "lg";
  showTagline?: boolean;
}) {
  const text =
    size === "lg" ? "text-2xl" : size === "sm" ? "text-sm" : "text-base";
  const box =
    size === "lg" ? "w-11 h-11 rounded-2xl" : size === "sm" ? "w-7 h-7 rounded-lg" : "w-9 h-9 rounded-xl";
  const mark =
    size === "lg" ? "w-6 h-6" : size === "sm" ? "w-4 h-4" : "w-5 h-5";

  return (
    <div className="flex items-center gap-2.5">
      <span className={`relative ${box} flex items-center justify-center bg-brand-700/25 border border-brand-600/30 text-[#22d3ee]`}>
        <span className={`absolute inset-0 ${box.split(" ").slice(-1)} bg-[#22d3ee]/10 blur-sm`} />
        <NovaMark className={`relative ${mark}`} />
      </span>
      <span className="flex flex-col leading-none">
        <span className={`font-mono font-bold tracking-tight text-white ${text}`}>NovaSynth</span>
        {showTagline && (
          <span className="mt-1 text-[10px] font-medium uppercase tracking-[0.22em] text-slate-500">
            Competitive Intelligence
          </span>
        )}
      </span>
    </div>
  );
}

/** Full-page aurora + grid backdrop used behind glass cards on every screen. */
export function AuroraBackdrop() {
  return (
    <>
      <div className="ns-aurora" aria-hidden="true" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        aria-hidden="true"
        style={{
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.5) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
          maskImage: "radial-gradient(ellipse at 50% 0%, black, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 0%, black, transparent 75%)",
        }}
      />
    </>
  );
}

export interface IntelAgent {
  key: string;
  name: string;
  Icon: LucideIcon;
  blurb: string;
  /** tailwind text color */
  tint: string;
  /** tailwind bg/border tint for the icon chip */
  chip: string;
  /** raw hex used for inline glows */
  hex: string;
  /**
   * Tasteful Amazon Nova model branding for the AWS Builder Center audience -
   * the product-facing Nova model NAME only (never a raw model ID / infra).
   * Mapping verified against novasynth/config.py + agents/*.
   */
  model: string;
}

/**
 * Marketing-facing intel agents. Five capabilities, branded by job only.
 * (The realtime panel renders four live agents; Threat Scoring is the
 * synthesis layer that scores them - shown here as a product capability.)
 */
export const INTEL_AGENTS: IntelAgent[] = [
  {
    key: "news",
    name: "News Monitor",
    Icon: Newspaper,
    blurb: "Breaking coverage, announcements, and market-moving news - surfaced the moment it lands.",
    tint: "text-amber-300",
    chip: "bg-amber-500/10 border-amber-500/25",
    hex: "#f59e0b",
    model: "Amazon Nova 2 Lite + grounding",
  },
  {
    key: "document",
    name: "Document Intel",
    Icon: FileSearch,
    blurb: "Filings, financials, and earnings parsed into the numbers that actually move strategy.",
    tint: "text-blue-300",
    chip: "bg-blue-500/10 border-blue-500/25",
    hex: "#3b82f6",
    model: "Amazon Nova 2 Lite",
  },
  {
    key: "web",
    name: "Web Signals",
    Icon: Globe,
    blurb: "Pricing pages, reviews, and hiring signals tracked for the changes competitors won't announce.",
    tint: "text-cyan-300",
    chip: "bg-cyan-500/10 border-cyan-500/25",
    hex: "#22d3ee",
    model: "Amazon Nova 2 Lite + grounding",
  },
  {
    key: "video",
    name: "Video Intel",
    Icon: Video,
    blurb: "Launches, demos, and keynotes watched and summarised so you never miss the reveal.",
    tint: "text-purple-300",
    chip: "bg-purple-500/10 border-purple-500/25",
    hex: "#a855f7",
    model: "Amazon Nova Lite",
  },
  {
    key: "threat",
    name: "Threat Scoring",
    Icon: ShieldAlert,
    blurb: "Every signal fused into one clear threat score and a battle-ready brief you can act on.",
    tint: "text-emerald-300",
    chip: "bg-emerald-500/10 border-emerald-500/25",
    hex: "#22c55e",
    model: "Amazon Nova 2 Lite",
  },
];
